import cn from 'classnames'
import type { ReactNode } from 'react'

import styles from './CockpitRow.module.scss'

interface IProps {
  title: string
  description?: ReactNode
  count?: number
  leading?: ReactNode
  trailing?: ReactNode
  badge?: ReactNode
  chevron?: boolean
  selectable?: boolean
  tone?: 'neutral' | 'attention'
  ariaLabel?: string
  className?: string
  onSelect?: () => void
}

/** Shared row layout; callers provide any priority-specific decorations and actions. */
export const CockpitRow = ({
  title,
  description,
  count,
  leading,
  trailing,
  badge,
  chevron = false,
  selectable = true,
  tone = 'neutral',
  ariaLabel,
  className,
  onSelect,
}: IProps) => (
  <div
    role={selectable ? 'button' : undefined}
    tabIndex={selectable ? 0 : undefined}
    aria-label={ariaLabel}
    className={cn(
      styles.card,
      { [styles.neutral]: tone === 'neutral', [styles.static]: !selectable },
      className
    )}
    onClick={selectable ? onSelect : undefined}
    onKeyDown={
      selectable
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onSelect?.()
            }
          }
        : undefined
    }
  >
    {badge}
    <span className={styles.cardTop}>
      {count !== undefined && (
        <span className={cn(styles.count, { [styles.longCount]: String(count).length > 3 })}>
          {count}
        </span>
      )}
      {leading}
      <span className={styles.cardText}>
        <span className={styles.cardTitle} title={title}>
          {title}
        </span>
        {description && <span className={styles.cardDescription}>{description}</span>}
      </span>
      {trailing}
      {chevron && (
        <i className={cn('kbase kbase-chevron-right', styles.chevron)} aria-hidden="true" />
      )}
    </span>
  </div>
)

CockpitRow.displayName = 'CockpitRow'
